import {
  List,
  TextInput,
  Datagrid,
  TextField,
  DateField,
  DateInput,
  useListContext,
  ListContextProvider,
  FunctionField,
  Button,
  SimpleList,
  ReferenceField
} from "react-admin";
import EmployeesField from "server/components/Fields/EmployeesField";
import { EmployeeServicePath, STAFF_REQUEST, StaffRequestTypes, VACATIONS, MOBILE_BREAKPOINT, StaffRequestStatuses } from "utils/constants";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { EmptyList } from "server/components";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import VacationPeriodsExpanded from "../components/VacationPeriodsExpanded";
import VacationPeriodsMobileExpanded from "../components/VacationPeriodsMobileExpanded";
import { flattenLeaveRecord } from "../components/flattenLeaveRecord";
import { staffRequestStatusLabel } from "../components/staffRequestStatusLabel";
import CancelRequestDialog from "../components/CancelRequestDialog";
import { employeeRequests } from "api/employeeApi";

const filters = [
  <TextInput
    label="Име"
    source="name"
    alwaysOn
  />,
  <DateInput
    label="От дата"
    source="startDate"
    alwaysOn
  />,
  <DateInput
    label="До дата"
    source="endDate"
    alwaysOn
  />,
];

const statusLabel = rec => staffRequestStatusLabel(rec?.staffRequest?.status);

const RowActions = ({ rec, navigate, iconOnly = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      alignItems: "center",
      justifyItems: "center",
    }}
  >
    <Box>
      <Button
        label={iconOnly ? "" : "Детайли"}
        onClick={() =>
          navigate(`/${EmployeeServicePath}details/${STAFF_REQUEST}/${rec?.leaveId}`, {
            state: { type: StaffRequestTypes.AnnualLeave, readOnly: true },
          })
        }
      >
        <VisibilityIcon />
      </Button>
    </Box>
    <Box>
      {rec?.staffRequest?.status === StaffRequestStatuses.Pending && (
        <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
          <Button
            label={iconOnly ? "" : "Редактиране"}
            onClick={() => navigate(`/${EmployeeServicePath}edit/${VACATIONS}/${rec?.leaveId}`)}
          >
            <EditIcon />
          </Button>
        </SecuredComponent>
      )}
      {rec?.staffRequest?.status === StaffRequestStatuses.Approved && (
        <SecuredComponent permission={permissions.canCreateLeaveRequests}>
          <Button
            label={iconOnly ? "" : "Коригиране"}
            onClick={() => navigate(`/${EmployeeServicePath}correct/${VACATIONS}/${rec?.leaveId}`)}
          >
            <EditIcon />
          </Button>
        </SecuredComponent>
      )}
    </Box>
    <Box>
      <SecuredComponent permission={permissions.canUpdateLeaveRequests}>
        <CancelRequestDialog
          record={rec}
          requestType={StaffRequestTypes.AnnualLeave}
          iconOnly={iconOnly}
        />
      </SecuredComponent>
    </Box>
  </Box>
);

const MobileView = ({ navigate }) => (
  <SimpleList
    empty={<EmptyList />}
    linkType={false}
    sx={settings.style.mobileList}
    primaryText={rec => (
      <div>
        <MobileListRow fields={[{ label: "№", value: rec.id }]} />
        <MobileListRow fields={[{ label: "Именa", value: <EmployeesField source="employeeId" /> }]} />
      </div>
    )}
    secondaryText={rec => (
      <>
        <div>
          <MobileListRow
            fields={[{ label: "Начална дата", value: <DateField source="startDate" locales="bg-BG" emptyText="—" /> }]}
          />
          <MobileListRow
            fields={[{ label: "Крайна дата", value: <DateField source="endDate" locales="bg-BG" emptyText="—" /> }]}
          />
          <MobileListRow fields={[{ label: "Работни дни", value: rec?.numberOfDays }]} />
          <MobileListRow fields={[{ label: "Статус", value: statusLabel(rec) }]} />
          <MobileListRow fields={[{ label: "Заместник", value: <EmployeesField source="substituteEmployeeIds" /> }]} />
          <MobileListRow
            fields={[
              {
                label: "Дата на подаване",
                value: <DateField source="staffRequest.createdOn" locales="bg-BG" emptyText="—" />,
              },
            ]}
          />
          <MobileListRow
            fields={[
              {
                label: "Основание",
                value: (
                  <ReferenceField
                    source="reasonId"
                    reference={employeeRequests.getLeaveReasonsSelect()}
                    link={false}
                  >
                    <FunctionField render={rec => (rec.note ? rec.note : rec.name)} />
                  </ReferenceField>
                ),
              },
            ]}
          />
        </div>
        <VacationPeriodsMobileExpanded record={rec} />
      </>
    )}
    tertiaryText={rec => (
      <RowActions
        rec={rec}
        navigate={navigate}
        iconOnly
      />
    )}
  />
);

const DesktopView = ({ navigate }) => (
  <Datagrid
    sx={{
      "& .RaDatagrid-headerCell": { textAlign: "left" },
      "& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type": { textAlign: "center" },
    }}
    expand={<VacationPeriodsExpanded />}
    expandSingle
    isRowExpandable={rec => (rec.periods?.length || 0) > 1}
    bulkActionButtons={false}
    rowClick={false}
  >
    <TextField
      source="id"
      label="№"
    ></TextField>
    <EmployeesField
      source="employeeId"
      label="Именa"
      sortable={false}
    />
    <EmployeesField
      source="substituteEmployeeIds"
      label="Заместник"
      sortable={false}
    />
    <DateField
      source="staffRequest.createdOn"
      label="Дата на подаване"
      locales="bg-BG"
      emptyText="—"
      sortable={false}
    ></DateField>
    <DateField
      source="startDate"
      label="Начална дата"
      locales="bg-BG"
      emptyText="—"
      sortable={false}
    ></DateField>
    <DateField
      source="endDate"
      label="Крайна дата"
      locales="bg-BG"
      emptyText="—"
      sortable={false}
    ></DateField>
    <TextField
      source="numberOfDays"
      label="Работни дни"
      sortable={false}
    ></TextField>
    <ReferenceField
      source="reasonId"
      reference={employeeRequests.getLeaveReasonsSelect()}
      label="Основание"
      sortable={false}
      link={false}
    >
      <FunctionField render={rec => (rec.note ? rec.note : rec.name)} />
    </ReferenceField>
    <FunctionField
      label="Статус"
      sortable={false}
      render={statusLabel}
    />
    <FunctionField
      label="Действия"
      source="leaveId"
      render={rec => (
        <RowActions
          rec={rec}
          navigate={navigate}
        />
      )}
    />
  </Datagrid>
);

const ModifiedVacationDisplayData = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const listContext = useListContext();
  const modifiedData = (listContext.data || []).map(flattenLeaveRecord);

  // FE filtering by ID, since they appear too random now
  if (listContext.sort?.field === 'id') {
    modifiedData.sort((a, b) =>
      listContext.sort.order === 'ASC' ? a.id - b.id : b.id - a.id
    );
  }

  return (
    <ListContextProvider value={{ ...listContext, data: modifiedData }}>
      {isMobile ? <MobileView navigate={navigate} /> : <DesktopView navigate={navigate} />}
    </ListContextProvider>
  );
};

const VacationsList = () => {
  return (
    <List
      title="Отпуски"
      filters={filters}
      exporter={false}
    >
      <ModifiedVacationDisplayData />
    </List>
  );
};
export default VacationsList;
